// Pre-existing native import - exempt from multi-version OTA adapter layer requirement.
// New usages should be migrated to the adapter layer.
// eslint-disable-next-line no-restricted-imports
import { CameraDirection, CameraSource } from "@capacitor/camera";
import { isDefined } from "@clipboard-health/util-ts";
import { useTheme } from "@mui/material";
import { FullScreenDialog, isCapacitorPlatform, useToast } from "@src/appV2/lib";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { IGNORE_CAMERA_ERROR_MESSAGES } from "@src/appV2/lib/files/constants";
import { CameraPermissionDeniedError } from "@src/appV2/lib/Media/CameraPermissionDeniedError";
import { type UploadFileData } from "@src/appV2/lib/Media/types";
import { useTakePhoto } from "@src/appV2/lib/Media/useTakePhoto";
import type { UseModalState } from "@src/appV2/redesign/system/ui-components";
import { useCallback, useState } from "react";
import { useHistory } from "react-router-dom";

import { WorkBadgeReviewPolicySource } from "../../../../WorkBadges/components/Policy/types";
import { WorkBadgeReviewPolicyStep } from "../../../../WorkBadges/hooks/useWorkBadgeReviewPolicy";
import { getWorkBadgeReviewPolicyUrl } from "../../../../WorkBadges/utils/getWorkBadgeReviewPolicyUrl";
import { BadgeVerificationConfirmContent } from "./BadgeVerificationConfirmContent";
import { BadgeVerificationIntroContent } from "./BadgeVerificationIntroContent";
import { BADGE_VERIFICATION_IMAGE_WIDTH } from "./constants";
import { useBadgeVerificationSubmit } from "./hooks/useBadgeVerificationSubmit";
import { NoBadgeContent } from "./NoBadgeContent";
import { type BadgeVerificationPhoto, BadgeVerificationStep } from "./types";

interface BadgeVerificationDialogProps {
  shiftId: string;
  qualificationName: string;
  modalState: UseModalState;
  onSubmitPhoto: () => void;
  onSkipBadgeVerification: () => void;
}

export function BadgeVerificationDialog(props: BadgeVerificationDialogProps) {
  const { shiftId, qualificationName, modalState, onSubmitPhoto, onSkipBadgeVerification } = props;

  const history = useHistory();
  const theme = useTheme();
  const { showErrorToast } = useToast();
  const [step, setStep] = useState(BadgeVerificationStep.INTRO);
  const [photo, setPhoto] = useState<BadgeVerificationPhoto | undefined>(undefined);

  const { handleSubmit, handleSkipToClockIn, isSubmitting } = useBadgeVerificationSubmit({
    shiftId,
    photo,
    onSubmitPhoto,
    onSkipSuccess: onSkipBadgeVerification,
  });

  useLogEffect(
    APP_V2_USER_EVENTS.BADGE_VERIFICATION_DIALOG_VIEWED,
    { shiftId },
    { enabled: modalState.modalIsOpen }
  );

  const resetState = useCallback(() => {
    setStep(BadgeVerificationStep.INTRO);
    setPhoto(undefined);
  }, []);

  const takePhoto = useTakePhoto({
    source: isCapacitorPlatform() ? CameraSource.Camera : CameraSource.Photos,
    onSuccess: (photoData: UploadFileData) => {
      setPhoto({ ...photoData, previewUrl: photoData.dataUrlBase64 });
      setStep(BadgeVerificationStep.CONFIRM);
    },
    onError: (error) => {
      if (error instanceof CameraPermissionDeniedError) {
        showErrorToast("Camera permission is required to take a badge photo.");
      } else if (IGNORE_CAMERA_ERROR_MESSAGES.has(error.message)) {
        // User cancelled camera - don't show error toast
      } else {
        showErrorToast("Failed to take photo. Please try again.");
      }
    },
    photoOptions: {
      direction: CameraDirection.Front,
      width: BADGE_VERIFICATION_IMAGE_WIDTH,
    },
  });

  const handleClose = useCallback(() => {
    modalState.closeModal();
    setTimeout(resetState, theme.transitions.duration.leavingScreen);
  }, [modalState, resetState, theme.transitions.duration.leavingScreen]);

  return (
    <FullScreenDialog
      skipTopSafeAreaPadding
      modalState={modalState}
      variant="tertiary"
      // Disable transition so navigating back from policy page opens dialog instantly
      transitionDuration={0}
    >
      {step === BadgeVerificationStep.INTRO && (
        <BadgeVerificationIntroContent
          onBack={handleClose}
          onTakePhoto={() => {
            logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_TAKE_PHOTO_TAPPED, { shiftId });
            void takePhoto();
          }}
          onNoBadge={() => {
            logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_NO_BADGE_TAPPED, { shiftId });
            setStep(BadgeVerificationStep.NO_BADGE);
          }}
          onWhyBadgeRequired={() => {
            logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_WHY_BADGE_REQUIRED_TAPPED, { shiftId });
            modalState.closeModal();
            history.push(
              getWorkBadgeReviewPolicyUrl({
                source: WorkBadgeReviewPolicySource.BADGE_VERIFICATION,
                shiftId,
                qualificationName,
                step: WorkBadgeReviewPolicyStep.REVIEW_POLICY,
              })
            );
          }}
        />
      )}

      {step === BadgeVerificationStep.CONFIRM && isDefined(photo) && (
        <BadgeVerificationConfirmContent
          photoPreviewUrl={photo.previewUrl}
          isSubmitting={isSubmitting}
          onBack={() => {
            setStep(BadgeVerificationStep.INTRO);
            setPhoto(undefined);
          }}
          onSubmit={handleSubmit}
          onRetakePhoto={() => {
            logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_RETAKE_PHOTO_TAPPED, { shiftId });
            void takePhoto();
          }}
        />
      )}

      {step === BadgeVerificationStep.NO_BADGE && (
        <NoBadgeContent
          isSubmitting={isSubmitting}
          onBack={() => {
            logEvent(APP_V2_USER_EVENTS.BADGE_VERIFICATION_HAS_BADGE_TAPPED, { shiftId });
            setStep(BadgeVerificationStep.INTRO);
          }}
          onClose={handleClose}
          onSkipToClockIn={handleSkipToClockIn}
        />
      )}
    </FullScreenDialog>
  );
}
